import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import type { TFunction } from 'i18next'
import type { CumulativeAssessmentLockView } from '@/api/lcLearning'
import { Button } from '@/components/learner/ui/Button'
import type { CourseNameLabels } from '@/utils/courseName'
import { groupScopeChaptersByCourse } from '@/utils/cumulativeScope'
import type { CumulativeScopeGroup } from '@/utils/cumulativeScope'

/**
 * 누적 테스트 안내 — 진단 테스트의 수학머리 시작 화면(`diagnostic.start`)과 같은 골격을 쓴다.
 *
 * 레슨 테스트는 교사가 출력물로 진행하므로 학습자 화면에 줄을 두지 않는다(진단의 레벨 테스트와 같다).
 * 누적 테스트가 두 부분으로 이뤄진다는 사실은 eyebrow 가 알리고, 화면은 학습자가 지금 할 수 있는
 * 수학머리 테스트 하나에만 집중한다.
 */
export function CumulativeAssessmentNotice({
  assessment,
  onMathHead,
  t,
}: {
  assessment: CumulativeAssessmentLockView
  onMathHead: () => void
  t: TFunction
}) {
  const courseNames: CourseNameLabels = {
    core: (level) => t('today.cumulative.course.core', { level }),
    advanced: (level) => (level
      ? t('today.cumulative.course.advancedLevel', { level })
      : t('today.cumulative.course.advanced')),
    elite: () => t('today.cumulative.course.elite'),
  }
  const scopeGroups = groupScopeChaptersByCourse(assessment.scope.chapters, courseNames)

  const mathHead = assessment.mathHead
  const canStart = mathHead.canStartOrResume
  const resume = mathHead.status === 'IN_PROGRESS' && mathHead.latestRunId != null
  // 2회차 이상은 교사가 연 재시험이다. 시작 버튼만 "다시" 로 읽히게 하고 나머지 골격은 같다.
  const retake = !resume && (mathHead.roundNo ?? 1) > 1
  const ctaKey = resume ? 'today.cumulative.ctaResume' : retake ? 'today.cumulative.ctaRetake' : 'today.cumulative.cta'
  // 마감은 교사가 누른다. 학습자 쪽에서는 (1) 할 일이 남음 (2) 수학머리만 끝냄 (3) 전부 끝나 확인 대기 세 상태다.
  const state = assessment.awaitingTeacherResolution ? 'awaiting' : canStart ? 'todo' : 'mathHeadDone'
  const headline = {
    todo: 'today.cumulative.title',
    mathHeadDone: 'today.cumulative.mathHeadDoneTitle',
    awaiting: 'today.cumulative.awaitingTitle',
  }[state]
  const description = {
    todo: 'today.cumulative.description',
    mathHeadDone: 'today.cumulative.mathHeadDoneDescription',
    awaiting: 'today.cumulative.awaitingResolution',
  }[state]

  return (
    <div role="status" className="flex flex-col items-center gap-12">
      <div className="flex flex-col items-center gap-6">
        <div className="flex flex-col items-center gap-2">
          <p className="text-lc-v3-h4 font-semibold text-lc-text-link">
            {t('today.cumulative.eyebrow')}
          </p>
          <h1 id="cumulative-assessment-heading" className="text-lc-v3-h1 font-bold text-lc-text-primary">
            {t(headline)}
          </h1>
        </div>
        <p className="whitespace-pre-line text-lc-v3-body-1 font-medium text-lc-text-secondary">
          {t(description)}
        </p>
        <CumulativeScopeChip
          groups={scopeGroups}
          fallback={t('today.cumulative.scope', {
            start: assessment.scope.startChapterOrdinal,
            end: assessment.scope.endChapterOrdinal,
          })}
          t={t}
        />
      </div>
      {canStart ? (
        <Button size="XL" className="px-10" onClick={onMathHead}>
          {t(ctaKey)}
        </Button>
      ) : null}
    </div>
  )
}

/**
 * 시험범위 — 누적 리포트와 같은 `과정 · 챕터 N. 제목` 나열을 진단 화면의 문항 수 칩 자리에 담는다.
 * 범위가 길면 칩 안에서 줄바꿈되므로 모서리는 알약이 아니라 둥근 사각형을 쓴다.
 */
function CumulativeScopeChip({
  groups,
  fallback,
  t,
}: {
  groups: CumulativeScopeGroup[]
  fallback: string
  t: TFunction
}) {
  return (
    <p className="flex max-w-[680px] flex-wrap items-center justify-center gap-x-5 gap-y-1.5 rounded-2xl bg-lc-v3-gray-100 px-5 py-3 text-lc-v3-body-2">
      {groups.length === 0 ? (
        <span className="font-medium text-lc-text-secondary">{fallback}</span>
      ) : groups.map((group) => (
        <span
          key={`${group.courseName ?? 'no-course'}-${group.chapters[0].ordinal}`}
          className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5"
        >
          {group.courseName ? (
            <span className="font-semibold text-lc-text-link">{group.courseName}</span>
          ) : null}
          {group.chapters.map((chapter) => (
            <span key={chapter.ordinal} className="flex items-center gap-1.5">
              <span className="font-medium text-lc-text-tertiary">
                {t('today.cumulative.chapterLabel', { chapter: chapter.ordinal })}
              </span>
              {chapter.title ? (
                <span className="font-medium text-lc-text-secondary"><ContentMetadataText text={chapter.title} /></span>
              ) : null}
            </span>
          ))}
        </span>
      ))}
    </p>
  )
}
